import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  useTheme,
  Typography,
  Link,
  useMediaQuery,
  Switch,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { AuthContext } from "../../context/AuthContext";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import { getAssessmentHistoryStyle } from "../../components/customstyles/AssessmentHistory";
import SubjectIcon from "@mui/icons-material/Subject";
import AssessmentGraph from "./AssessmentGraph";
import LearnMore from "../LearnMore/LearnMore";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { formatDateShortMonthName } from "../utilsFunctions.js";
import { useGuidedMode } from "../../providers/GuidedModeProvider";
import GridContainer from "../../components/grid/GridContainer.jsx";
import GridItem from "../../components/grid/GridItem.jsx";
import LoginError from "../public/LoginError.jsx";
const useStyles = () => {
  let theme = useTheme();
  return getAssessmentHistoryStyle(theme);
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {children}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function getStatusLabel(status) {
  switch (status) {
    case 1:
    case 2:
    case 3:
      return "Incomplete";
    case 4:
      return "Complete";
    default:
      return "Unknown";
  }
}

function getChipLabel(item) {
  return item.status === 4 ? `Score: ${item.score}` : "Incomplete";
}

function getChipBackgroundColor(item) {
  if (item.status === 1 || item.status === 2 || item.status === 3) {
    return { color: "#BF360C", backgroundColor: "#FFF8E1", fontWeight: 600 };
  } else {
    return { color: "#0D47A1", backgroundColor: "#E3F2FD", fontWeight: 600 };
  }
}

function AssessmentHistory({ loadingText = "Loading..." }) {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const history = useHistory();
  const match = useRouteMatch();
  const [value, setValue] = useState(0);
  const [loadingStarted, setLoading] = useState(true);
  const [isAnyError, setError] = useState(false);
  const [patientName, setPatientName] = useState(null);
  const [patientDOB, setPatientDOB] = useState(null);
  const [patientEmrId, setPatientEmrId] = useState(null);
  const [openSnacbar, setOpenSnacbar] = useState(false);
  const [patientsAssessmentsData, setPatientsAssessmentsData] = useState([]);
  const { makeAuthenticatedApiCall } = useContext(AuthContext);
  const location = useLocation();
  const { guidedMode, handleSwitchChange, refreshGuidedMode } = useGuidedMode();

  const handleChange = (event, newValue) => {
    setValue(newValue);
    let addResult = newValue + 1;
    history.push(`/patients/${match.params.patientId}/${addResult}`);
  };
  const checkTabIndex = () => {
    const tabIndex = window.location.pathname.split("/").pop();
    if (tabIndex == 2) {
      setValue(tabIndex - 1);
    }
  };
  useEffect(() => {
    checkTabIndex();
    const fetchPatientData = async () => {
      refreshGuidedMode();
      setLoading(true);
      if (location.state?.patientAdded) {
        setOpenSnacbar(true);
      }
      try {
        const addBy = localStorage.getItem("addedThroughSource");
        let url = `/api/userservice/getpatientdetails/${match.params.patientId}`;
        if (addBy) {
          url += `?addedThrough=${encodeURIComponent(addBy)}`;
        }
        const patientDetailsResponse = await makeAuthenticatedApiCall(
          "get",
          url,
          {}
        );
        if (
          patientDetailsResponse?.status === 200 &&
          patientDetailsResponse?.data
        ) {
          const patientData = patientDetailsResponse.data;
          setPatientName(patientData.patientName);
          setPatientDOB(formatDateShortMonthName(patientData.birthdate));
          setPatientEmrId(patientData.emrPatientId);
          const assessmentsResponse = await makeAuthenticatedApiCall(
            "get",
            `/api/userservice/getPatientAssessments/${match.params.patientId}`,
            {}
          );

          if (assessmentsResponse?.status === 200 && assessmentsResponse.data) {
            setPatientsAssessmentsData(assessmentsResponse.data);
          } else {
            setPatientsAssessmentsData([]);
          }
        } else {
          setError(true);
        }
      } catch (error) {
        console.error("Error fetching patient data:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPatientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.patientId, makeAuthenticatedApiCall]);

  const handleNavigateToAssessment = (assessmentId, status) => {
    if (assessmentId) {
      if (status === "Incomplete") {
        history.push({
          pathname: `/patients/${match.params.patientId}/assessment/${assessmentId}`,
        });
      } else {
        history.push({
          pathname: `/patients/${match.params.patientId}/assessment/result/${assessmentId}`,
          // search: `?assessmentId=${assessmentId}`,
        });
      }
    } else {
      history.push({
        pathname: `/patients/${match.params.patientId}/assessment`,
      });
    }
  };
  const handleCloseSnackbar = () => {
    setOpenSnacbar(false);
  };
  const handleNameClick = () => {
    history.push(`/patients/${match.params.patientId}/1`);
    setValue(0);
  };
  return loadingStarted ? (
    <Box sx={styles.loadingCoverageStyle}>
      <Box sx={{ marginBottom: "20px" }}>
        <CircularProgress />
      </Box>
      <Typography sx={styles.loadinText}>{loadingText}</Typography>
    </Box>
  ) : (
    <>
      {isAnyError ? (
        <LoginError isAssessmentError={true} />
      ) : (
        <div
          style={{
            backgroundColor: "#FAFAFA",
          }}
        >
          <Box
            sx={{
              width: "100%",
              padding: isMobile ? "20px" : isLarge ? "50px 200px" : "40px",
            }}
          >
            {isMobile ? (
              <Box sx={styles.backToPatientsListStyle}>
                <ChevronLeft color="black" />
                <Typography variant="p" sx={{ color: "#0D47A1" }}>
                  Back to Patients List
                </Typography>
              </Box>
            ) : (
              <Box sx={styles.headerStyle}>
                <Link href="/patients" underline="none">
                  <Typography
                    variant="body1"
                    sx={{ color: "#0D47A1", textDecoration: "underline" }}
                  >
                    Patients List
                  </Typography>
                </Link>
                <ChevronRight />
                {value !== 0 ? (
                  <Link
                    component="button"
                    underline="none"
                    onClick={() => {
                      handleNameClick();
                    }}
                    sx={{
                      cursor: "pointer",
                      color: "#0D47A1",
                      textDecoration: "underline",
                    }}
                  >
                    <Typography className="FSMask" variant="body1">
                      {patientName}
                    </Typography>
                  </Link>
                ) : (
                  <Typography className="FSMask" variant="body1">
                    {patientName}
                  </Typography>
                )}
                {value === 1 && (
                  <>
                    <ChevronRight />
                    <Typography variant="p">Learn More</Typography>
                  </>
                )}
              </Box>
            )}
            <GridContainer sx={styles.patientInfoStyle}>
              <GridItem lg={6} md={6} sm={5} xs={5}>
                <Typography variant="h4">{patientName}</Typography>
              </GridItem>
              <GridItem
                lg={3}
                md={3}
                sm={3}
                xs={3}
                sx={{
                  display: "flex",
                  marginRight: "5px",
                  justifyContent: "flex-end",
                }}
              >
                <Box display="flex" gap={1}>
                  <Switch
                    checked={guidedMode !== false}
                    onClick={handleSwitchChange}
                    color="primary"
                    size="small"
                  />
                  <Typography
                    sx={{
                      ...styles.tableColumnHeader,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Guided Mode
                  </Typography>
                </Box>
              </GridItem>
              <GridItem
                lg={2}
                md={2}
                sm={3}
                xs={3}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  sx={styles.buttonStyle(isMobile)}
                  onClick={() => handleNavigateToAssessment()}
                >
                  Start Assessment
                </Button>
              </GridItem>
              <GridItem lg={12} md={12} sm={12} xs={12}>
                <Box sx={{ mt: 2, display: "flex", color: "#263238", gap: 2 }}>
                  <Typography variant="p">
                    Date of Birth: {patientDOB}
                  </Typography>
                  <Typography variant="p">
                    EHR Patient ID: {patientEmrId}
                  </Typography>
                </Box>
              </GridItem>
            </GridContainer>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs"
              >
                <Tab label="Assessment History" {...a11yProps(0)} />
                <Tab label="Learn More" {...a11yProps(1)} />
              </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
              {patientsAssessmentsData.length > 0 ? (
                <Box sx={styles.tabPanelStyle}>
                  <AssessmentGraph
                    assessmentGraphData={patientsAssessmentsData}
                  />
                  <Typography variant="h5">
                    Assessment History{" "}
                    <span style={{ color: "#546E7A" }}>
                      ({patientsAssessmentsData.length})
                    </span>
                  </Typography>
                  <div className="FSUnmask">
                    <Box sx={{ mt: "30px" }}>
                      {patientsAssessmentsData.length > 0 ? (
                        patientsAssessmentsData.map((item, index) => {
                          return (
                            <Box key={index} sx={styles.cardStyle(isMobile)}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "125px",
                                  }}
                                >
                                  <Typography>
                                    {formatDateShortMonthName(
                                      item.assessmentDate.split("T")[0]
                                    )}
                                  </Typography>
                                  {item.status === 1 ||
                                  item.status === 2 ||
                                  item.status === 3 ? (
                                    <Typography
                                      variant="p"
                                      sx={{
                                        mt: 1,
                                        display:
                                          isMobile || isLarge
                                            ? "none"
                                            : "block",
                                        ...styles.expiresInStyle,
                                      }}
                                    >
                                      Expires in 24 hours
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Chip
                                  label={getChipLabel(item)}
                                  color="primary"
                                  sx={getChipBackgroundColor(item)}
                                />
                                {item.status === 1 ||
                                item.status === 2 ||
                                item.status === 3 ? (
                                  <Typography
                                    sx={{
                                      ...styles.expiresInStyle,
                                      display:
                                        isMobile || isLarge ? "block" : "none",
                                    }}
                                  >
                                    Expires in 24 hours
                                  </Typography>
                                ) : null}
                              </Box>
                              <Button
                                variant="outlined"
                                sx={styles.buttonOutlinedStyle(isMobile)}
                                onClick={() =>
                                  handleNavigateToAssessment(
                                    item.assessmentId,
                                    getStatusLabel(item.status)
                                  )
                                }
                              >
                                {getStatusLabel(item.status) === "Incomplete"
                                  ? "Continue Assessment"
                                  : "View Results"}
                              </Button>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography>No assessments found.</Typography>
                      )}
                    </Box>
                  </div>
                </Box>
              ) : (
                <div className="FSUnmask">
                  <Box sx={styles.noAssessmentBox}>
                    <Box sx={styles.noAssessmentInnerBox}>
                      <Box sx={styles.subjectIconBox}>
                        <SubjectIcon
                          sx={{ color: "white", fontSize: "30px" }}
                        ></SubjectIcon>
                      </Box>
                      <Typography variant="h6">No Assessment Yet</Typography>
                      <Typography variant="p" sx={{ color: "#546E7A" }}>
                        Start an assessment to see history
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ ...styles.buttonStyle(isMobile), mt: "20px" }}
                        onClick={() => handleNavigateToAssessment()}
                      >
                        Start Assessment
                      </Button>
                    </Box>
                  </Box>
                </div>
              )}
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <LearnMore />
            </CustomTabPanel>
          </Box>
          <Snackbar
            open={openSnacbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            // action={true}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              variant="filled"
              sx={{ width: "100%" }}
            >
              Patient has been successfully added!
            </Alert>
          </Snackbar>
        </div>
      )}
    </>
  );
}

export default AssessmentHistory;
